import styles from './BankingInfo.module.css';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import BankingForm from './BankingForm';
import StockTracing from './StockTracing';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

// 이미지 임포트
import StockBadge from "../../components/common/StockBadge";

Modal.setAppElement('#root');

function BankingInfo(props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { tmpKey, timeKey } = useParams();

    // ─── 날짜 문자열을 YYYY-MM-DD로 통일하는 유틸 ───
    const normalizeDateString = (dateString) => {
        if (!dateString) return '';
        if (/^\d{8}$/.test(dateString)) {
            const y = dateString.slice(0, 4);
            const m = dateString.slice(4, 6);
            const d = dateString.slice(6, 8);
            return `${y}-${m}-${d}`;
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
        }
        return dateString;
    };

    // times 문자열과 리스트의 item.time 을 동일 포맷으로 맞춰 비교
    const toTimeKey = (v) => {
        const s = String(v ?? '');
        // 이미 'YYYY-MM-DD HH:mm:ss.mmm' 형태면 그대로 사용
        if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3}$/.test(s)) return s;
        // 그 외(Date 타입/다른 문자열)면 기존 포맷터로 통일
        return formatDate(v);
    };

    // 이 상세가 보여줘야 하는 "그룹의 time 집합"
    const groupTimeSet = useMemo(() => {
        const ts = (props.item?.times || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
        return new Set(ts);
    }, [props.item?.times]);


    // ─── 빈 날짜 센티널 키 ───
    const EMPTY_DATE = '__EMPTY__';
    // '' / '0000-00-00' / '00000000' 등을 모두 EMPTY_DATE로 통일
    const toDateKey = (v) => {
        const raw = (v ?? '').toString().trim();
        if (raw === '' || raw === '0000-00-00' || raw === '00000000') return EMPTY_DATE;
        const norm = normalizeDateString(raw);
        return norm && norm.trim() !== '' ? norm : EMPTY_DATE;
    };

    // 현재 로그인한 사용자 정보
    const [userName, setUserName] = useState('');
    const [userInfo, setUserInfo] = useState({});

    useEffect(() => {
        fetch("/api/getLoginedUserInfo", {
            method: "POST",
            headers: { "Content-Type": "application/json" }
        })
            .then(res => res.json())
            .then(data => {
                setUserName(data.data[0]?.userName);
                setUserInfo(data.data[0]);
            });
    }, []);

    // ─── CoA PDF 다운로드 핸들러 ───
    const handleDownloadPdf = async (item) => {
        // 1. Lot Number 입력 모달 띄우기
        const { value: lotNumber } = await Swal.fire({
            title: t('Lot. Number 입력'),
            input: 'text',
            inputLabel: t('CoA 리포트에 기입될 Lot. Number를 입력하세요.'),
            inputPlaceholder: 'ex) 2405020109',
            showCancelButton: true,
            confirmButtonText: t('다운로드'),
            cancelButtonText: t('취소'),
            inputValidator: (value) => {
                if (!value) return t('Lot. Number를 입력해주세요!');
            }
        });

        // 사용자가 취소를 눌렀다면 함수 종료
        if (!lotNumber) return;

        try {
            // 2. 로딩 모달 표시
            Swal.fire({
                title: t('PDF 생성 중...'),
                text: t('잠시만 기다려주세요.'),
                allowOutsideClick: false,
                didOpen: () => { Swal.showLoading(); }
            });

            // 3. 백엔드 API 호출 (그래프 이미지는 null 처리)
            const response = await fetch('/api/download/report/coa', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sampleKey: tmpKey,
                    time: formatDate(item.time), // 현재 클릭한 뱅킹 row의 PK
                    lotNumber: lotNumber,        // 입력받은 Lot Number
                    graphImage: null             // 그래프 제외
                })
            });

            if (!response.ok) throw new Error('PDF generation failed');

            // 4. 파일 다운로드 처리
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            // 파일명 설정: CoA_오가노이드키_날짜.pdf
            const cleanTime = formatDate(item.time).replace(/[: ]/g, '');
            a.download = `CoA_${tmpKey.replace(/#/g, '')}_${cleanTime}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            Swal.close(); // 로딩 창 닫기

        } catch (error) {
            console.error('Download error:', error);
            Swal.fire(t('오류'), t('PDF 다운로드에 실패했습니다.'), 'error');
        }
    };

    // 엑셀 다운로드용 키 list
    const [selectedKeys, setSelectedKeys] = useState([]);

    // 데이터 리스트 등
    const [bankingList, setBankingList] = useState([{}]);
    const [patientCode, setPatientCode] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isTracingModalOpen, setIsTracingModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    // [추가] 트레이싱 모달 전용 State 생성
    const [tracingItem, setTracingItem] = useState(null);
    const [showStatsModal, setShowStatsModal] = useState(false);
    const openStatsModal = () => setShowStatsModal(!showStatsModal);
    const closeStatsModal = () => setShowStatsModal(false);
    const [showFilterModal, setShowFilterModal] = useState(false);

    // 필터 옵션
    const [passageNoOptions, setPassageNoOptions] = useState([]);
    const [cultureMediaOptions, setCultureMediaOptions] = useState([]);
    const [freezingDateOptions, setFreezingDateOptions] = useState([]);
    const [keeperOptions, setKeeperOptions] = useState([]);

    // 페이지네이션
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    // 필터 상태 (초기값: 부모에서 받은 그룹 item 기준)
    const [filterOptions, setFilterOptions] = useState({
        passageNo: props.item?.PassageNo,
        cultureMedia: props.item?.CultureMedia,
        freezingDate: toDateKey(props.item?.FreezingDate), // 공란이면 센티널
        keeper: props.item?.Keeper,
    });

    // 부모에서 그룹을 바꾸면 필터도 즉시 동기화
    useEffect(() => {
        setFilterOptions({
            passageNo: props.item?.PassageNo,
            cultureMedia: props.item?.CultureMedia,
            freezingDate: toDateKey(props.item?.FreezingDate),
            keeper: props.item?.Keeper,
        });
        setCurrentPage(1);
    }, [props.item]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterOptions(prev => ({ ...prev, [name]: value }));
    };

    // 체크박스 토글
    const handleCheckboxChange = (key, isChecked) => {
        setSelectedKeys(prev => isChecked ? [...prev, key] : prev.filter(k => k !== key));
    };

    // 시간을 ISO → 포맷팅 (기존 코드)
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
    };

    // fetch 헬퍼
    const apiCall = async (endpoint, options) => {
        const res = await fetch(endpoint, options);
        return await res.json();
    };

    // 데이터 불러오기
    const fetchData = useCallback(async (endpoint, setter) => {
        const data = await apiCall(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ param: tmpKey }),
        });
        setter(data);
    }, [tmpKey]);

    useEffect(() => {
        fetchData('/api/getBankingList', setBankingList);
        fetchData('/api/getPatientCode', setPatientCode);
    }, [fetchData]);

    useEffect(() => {
        setPassageNoOptions([...new Set(bankingList.map(i => i.PassageNo))]);
        setCultureMediaOptions([...new Set(bankingList.map(i => i.CultureMedia))]);
        // 날짜 옵션도 센티널 기준으로 통일
        setFreezingDateOptions([
            ...new Set(bankingList.map(i => toDateKey(i.FreezingDate)))
        ]);
        setKeeperOptions([...new Set(bankingList.map(i => i.Keeper))]);
    }, [bankingList]);

    // Mycoplasma 편집 모드
    const [editingMycoTime, setEditingMycoTime] = useState(null);
    const [mycoInput, setMycoInput] = useState('');
    const startEditMyco = (item) => {
        setEditingMycoTime(item.time);
        setMycoInput(item.Mycoplasma || '');
    };
    const cancelEditMyco = () => {
        setEditingMycoTime(null);
        setMycoInput('');
    };
    const saveMycoChange = async (item) => {
        const payload = {
            Organoid_Info_Key: tmpKey,
            time: formatDate(item.time),
            Mycoplasma: mycoInput
        };
        try {
            const result = await apiCall('/api/updateMycoplasma', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ param: payload, email: userInfo.email }),
            });
            if (result.error) alert(result.error);
            else {
                alert(result.message || 'Mycoplasma가 수정되었습니다.');
                fetchData('/api/getBankingList', setBankingList);
            }
        } catch (err) {
            console.error(err);
            alert('서버 통신 오류');
        } finally {
            cancelEditMyco();
        }
    };

    // 전체 목록 재조회
    const fetchBankingList = () => {
        fetch('/api/getBankingList', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ param: tmpKey })
        })
            .then(res => res.json())
            .then(data => setBankingList(data));
    };

    // 모달 열기/닫기
    const openModal = useCallback((item) => {
        if (item) navigate(`/organoid/${tmpKey}/bank/${formatDate(item.time)}`);
        setSelectedItem(item.exist ? null : item);
        setIsModalOpen(true);
    }, [tmpKey, navigate]);
    // ─── 모달 닫기 (수정됨) ───
    const closeModal = () => {
        setSelectedItem(null);
        setIsModalOpen(false);

        // [수정] 단순히 tmpKey로만 가면 BasicInfo로 갑니다. '/bank'를 꼭 붙여주세요.
        navigate(`/organoid/${tmpKey}/bank`);
    };

    useEffect(() => {
        const match = bankingList.find(i => formatDate(i.time) === timeKey);
        if (match && !isModalOpen) openModal(match);
    }, [bankingList, isModalOpen, timeKey, openModal]);
    // ─── 부모 추적 모달 열기/닫기 (수정됨) ───
    const openTracingModal = (item) => {
        setTracingItem(item); // [수정] selectedItem 대신 tracingItem 사용
        setIsTracingModalOpen(true);
    };
    const closeTracingModal = () => {
        setTracingItem(null); // [수정] 닫을 때 초기화
        setIsTracingModalOpen(false);
    };

    // CRUD 핸들러 (기존 로직 유지)
    const handleRetrieve = async (newItem) => {
        Object.assign(newItem, { Vessel: '', Line: '', Box: '', Position: '0' });
        newItem.time = formatDate(newItem.time);
        try {
            const res = await fetch('/api/updateBankingInfo', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ param: newItem }),
            });
            const data = await res.json();
            if (data.message) alert(data.message);
            fetchBankingList();
            closeModal();
            return data.message;
        } catch (err) {
            console.error(err);
            alert('서버 통신 오류');
            return 'err';
        }
    };

    const handleDelete = (itemToDelete) => {
        itemToDelete.time = formatDate(itemToDelete.time);
        fetch('/api/deleteBankingInfo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ param: itemToDelete })
        })
            .then(res => res.json())
            .then(data => {
                alert(data?.message || '삭제 실패');
                fetchBankingList();
            });
        closeModal();
    };

    const handleSave = (newItem, isEditMode) => {
        // newItem.PatientCode = patientCode[0]?.PatientCode;
        // newItem.Organoid_Info_Key = tmpKey;

        // 비어있을 때만 보조값 사용
        newItem.PatientCode = newItem.PatientCode ?? patientCode[0]?.PatientCode ?? '';
        // Organoid_Info_Key는 사용자가 입력한 값을 우선, 없으면 tmpKey 사용
        const keySrc = newItem.Organoid_Info_Key ?? tmpKey ?? '';
        // # → hashmark 변환(백엔드 안전망은 있어도 프론트에서 한 번 더)
        newItem.Organoid_Info_Key = String(keySrc).replace(/#/g, 'hashmark');
        const endpoint = isEditMode ? '/api/updateBankingInfo' : '/api/insertBankingInfo';
        fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ param: newItem }),
        })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'duplication') {
                    alert('해당 위치에 이미 데이터가 저장되어 있습니다.');
                } else if (data.message === 'All positions are occupied') {
                    alert('모든 위치가 차지되어 있습니다.');
                } else if (data === 'err') {
                    alert(isEditMode ? '업데이트에 실패했습니다.' : '데이터 입력에 실패했습니다.');
                } else {
                    console.log(newItem);
                    alert(data.message);
                    fetchBankingList();
                    closeModal();
                }
            });
    };

    // ─── 필터링: 날짜는 센티널 키로 정확히 일치 비교 ───
    const filteredList = bankingList.filter(item => {
        // 1) 가장 정확한 기준: 이 그룹의 times 에 포함된 row 만
        if (groupTimeSet.size > 0) {
            return groupTimeSet.has(toTimeKey(item.time));
        }
        // 2) times 가 없을 예외 상황일 때만 기존 키 비교 (추가로 Mycoplasma/thawingNo 도 맞춰줌)
        const itemDateKey = toDateKey(item.FreezingDate);
        const filterDateKey = toDateKey(filterOptions.freezingDate);
        const sameBasic =
            (!filterOptions.passageNo || item.PassageNo === filterOptions.passageNo) &&
            (!filterOptions.cultureMedia || item.CultureMedia === filterOptions.cultureMedia) &&
            (itemDateKey === filterDateKey) &&
            (!filterOptions.keeper || item.Keeper === filterOptions.keeper);
        const sameMyco = (props.item?.Mycoplasma ?? '') === (item.Mycoplasma ?? '');
        const sameThaw = String(props.item?.thawingNo ?? '') === String(item.thawingNo ?? '');
        return sameBasic && sameMyco && sameThaw;
    });

    const paginatedFiltered = filteredList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const handlePageChange = (page) => setCurrentPage(page);

    return (
        <div className={styles.bankingInfo}>
            <div className={styles.bankingInfoBottom}>
                <div className={styles.bankingTabLeftSide}>

                    {/* [수정] 3개의 div를 지우고, 높이가 지정된 하나의 Spacer로 대체 */}
                    <div className={styles.bankingLeftSpacer}></div>

                    {/* 기존 제목들 시작 */}
                    <div className={styles.bankingLeftTitle}>Stock</div>
                    <div className={styles.bankingLeftTitle}>Patient Code</div>
                    <div className={styles.bankingLeftTitle}>Organoid Key</div>
                    <div className={styles.bankingLeftTitle}>Lab Code</div>
                    <div className={styles.bankingLeftTitle}>Passage No.</div>
                    <div className={styles.bankingLeftTitle}>Culture Media</div>
                    <div className={styles.bankingLeftTitle}>Freezing Date</div>
                    <div className={styles.bankingLeftTitle}>Keeper</div>
                    <div className={styles.bankingLeftTitle}>Freezing Media</div>
                    <div className={styles.bankingLeftTitle}>Dome/Vial or Cell Number</div>
                    <div className={styles.bankingLeftTitle}>Mycoplasma</div>
                    <div className={styles.bankingLeftTitle}>Thawing No.</div>
                    <div className={styles.bankingLeftTitleNote}>Note</div>
                    {/* <div className={styles.bankingLeftTitle}>Vials Number</div> */}
                    <div className={styles.bankingLeftTitle}>Exporter</div>
                    <div className={styles.bankingLeftTitle}>Export Date</div>
                    <div className={styles.bankingLeftTitle}>Storage</div>
                    <div className={styles.bankingLeftTitle}>Line</div>
                    <div className={styles.bankingLeftTitle}>Box</div>
                    <div className={styles.bankingLeftTitle}>Position</div>
                </div>

                {paginatedFiltered.map((item, idx) => {
                    // [추가] 부모/자식 유무에 따른 화살표 결정 로직
                    // hasParent, hasChild는 백엔드 쿼리에서 0 또는 1(혹은 count)로 넘어옴
                    const parentMark = item.hasParent ? '↑' : '\u00A0'; // \u00A0는 공백(&nbsp;)
                    const childMark = item.hasChild > 0 ? '↓' : '\u00A0';

                    return (
                        <div className={styles.bankingTabRightSide} key={item.time || idx}>
                            {/* 1. PDF 다운로드 버튼 */}
                            <div className={styles.bankingInfoDataButton}>
                                <div
                                    className={styles.pdfDownloadBtn}
                                    onClick={() => handleDownloadPdf(item)}
                                    title={t("리포트 다운로드")}
                                >
                                    📄 {t('PDF 다운로드')}
                                </div>
                            </div>
                            {/* 2. Edit / Trace 버튼 */}
                            <div className={styles.bankingInfoDataButton} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <button onClick={() => openModal(item)} className={styles.bankingHeaderButton}>Edit</button>
                                <button onClick={e => { e.stopPropagation(); openTracingModal({ time: formatDate(item.time) }); }} className={styles.bankingHeaderButton}>Trace</button>
                                
                                {/* [추가] 부모/자식 관계 시각화 표시 */}
                                <div style={{ 
                                    fontSize: '12px', 
                                    fontWeight: 'bold', 
                                    color: '#555', 
                                    whiteSpace: 'nowrap',
                                    marginLeft: '2px',
                                    fontFamily: 'monospace' // 화살표 정렬을 위해 고정폭 글꼴 추천
                                }}>
                                    [{parentMark} , {childMark}]
                                </div>
                            </div>
                            {/* 3. 자식 스탁 추가 버튼 */}
                            <div className={styles.bankingInfoDataButton}>
                                <button onClick={e => { e.stopPropagation(); openModal({ mode: 'add', time: formatDate(item.time) }); }} className={styles.bankingHeaderButton}>{t('자식 스탁 추가')}</button>
                            </div>

                            {/* 데이터 시작 (Master Stock 부터 줄이 맞게 됨) */}
                            <div className={styles.bankingInfoData}>
                                <StockBadge value={item.Master} />
                            </div>
                            <div className={styles.bankingInfoData}>{item.PatientCode || '-'}</div>
                            <div className={styles.bankingInfoData}>{item.Organoid_Info_Key?.replace('hashmark', '#') || '-'}</div>
                            <div className={styles.bankingInfoData}>{item.LabCode || '-'}</div>
                            <div className={styles.bankingInfoData}>{item.PassageNo || '-'}</div>
                            <div className={styles.bankingInfoData}>{item.CultureMedia || '-'}</div>

                            {/* 날짜 표시 */}
                            <div className={styles.bankingInfoData}>
                                {normalizeDateString(item.FreezingDate) || '-'}
                            </div>
                            <div className={styles.bankingInfoData}>{item.Keeper || '-'}</div>
                            <div className={styles.bankingInfoData}>{item.FreezingMedia || '-'}</div>
                            <div className={styles.bankingInfoData}>{item.domePerVialOrCellNumber || '-'}</div>

                            {/* Mycoplasma 편집 */}
                            <div className={styles.bankingInfoData}>
                                {editingMycoTime === item.time ? (
                                    <div className={styles.mycoEditContainer}>
                                        <input type="text" value={mycoInput} onChange={e => setMycoInput(e.target.value)} className={styles.mycoInput} />
                                        <button onClick={() => saveMycoChange(item)} className={styles.mycoSaveButton}>저장</button>
                                        <button onClick={cancelEditMyco} className={styles.mycoCancelButton}>취소</button>
                                    </div>
                                ) : (
                                    <div className={styles.mycoDisplayContainer}>
                                        <span>{item.Mycoplasma || '-'}</span>
                                        <button onClick={() => startEditMyco(item)} className={styles.mycoEditButton}>✏️</button>
                                    </div>
                                )}
                            </div>

                            <div className={styles.bankingInfoData}>{item.thawingNo || '-'}</div>
                            <div className={styles.textDiv}>
                                <textarea className={styles.bankingInfoDataAuto} disabled value={item.annotation || '-'} />
                            </div>
                            <div className={styles.bankingInfoData}>{item.Exporter || '-'}</div>

                            {/* 요청대로 ExporteDate 컬럼명 유지 */}
                            <div className={styles.bankingInfoData}>
                                {normalizeDateString(item.ExporteDate) || '-'}
                            </div>

                            <div className={styles.bankingInfoData}>{item.Vessel || '-'}</div>
                            <div className={styles.bankingInfoData}>{item.Line || '-'}</div>
                            <div className={styles.bankingInfoData}>{item.Box || '-'}</div>
                            <div className={styles.bankingInfoData}>{item.Position || '-'}</div>
                        </div>
                    );
                })}
            </div>

            {/* 페이지네이션 */}
            <div className={styles.pagination}>
                {Array.from({ length: Math.ceil(filteredList.length / itemsPerPage) }, (_, i) => i + 1).map(page => (
                    <button
                        key={page}
                        className={page === currentPage ? styles.activePage : ''}
                        onClick={() => setCurrentPage(page)}>
                        {page}
                    </button>
                ))}
            </div>

            {/* 모달: Tracing */}
            <Modal isOpen={isTracingModalOpen} onRequestClose={closeTracingModal} style={customStyles}>
                {/* [수정] time={selectedItem?.time} -> time={tracingItem?.time} 로 변경 */}
                <StockTracing
                    time={tracingItem?.time}
                    onCancel={closeTracingModal}
                    onSelectStock={(key, newTime) => {
                        // 트레이싱 내에서 다른 박스를 눌러 기준을 바꿀 때도 tracingItem을 업데이트
                        setTracingItem(prev => ({ ...prev, time: newTime }));
                    }}
                />
            </Modal>

            {/* 모달: Form */}
            <Modal isOpen={isModalOpen} onRequestClose={closeModal} style={customStyles}>
                <h2>
                    {selectedItem?.mode === "add"
                        ? 'Add Banking Information under'
                        : selectedItem
                            ? 'Edit Banking Information'
                            : 'Add Banking Information'}
                </h2>
                <BankingForm
                    mode={selectedItem?.mode}
                    time={selectedItem?.time}
                    item={selectedItem}
                    onSave={handleSave}
                    onCancel={closeModal}
                    onDelete={handleDelete}
                    onRetrieve={handleRetrieve}
                    isEditMode={selectedItem?.mode === "add" ? false : !!selectedItem}
                    patientCode={patientCode[0]?.PatientCode || ""}
                />
            </Modal>
        </div>
    );
}

const customStyles = {
    content: {
        width: 'fit-content',
        height: 'fit-content',
        margin: 'auto',
    },
};

export default BankingInfo;